import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  IconChevronDown,
  IconChevronUp,
  IconFileText,
  IconHistory,
} from "@tabler/icons-react";
import { Center, Loader } from "@mantine/core";
import {
  useAddCustomerNoteMutation,
  useCustomerNotesHistoryQuery,
  useUpdateCustomerSettingsMutation,
} from "../../api/queries/customerRepresentativeQueries";
import { useAuth } from "../../hooks/useAuth";
import { languageOptions } from "../../utils/consts";
import { CustomerNoteHistoryItem } from "./CustomerNoteHistoryItem";
import { CustomerNoteBody } from "./CustomerNoteBody";
import { MentionTextarea } from "./MentionTextarea";
import { CustomerNoteAttachmentPicker } from "./CustomerNoteAttachmentPicker";

type CustomerNotesHistorySectionProps = {
  customerOrgId: number;
  language?: string;
  onCustomerUpdate?: () => void;
  defaultHistoryOpen?: boolean;
  queriesEnabled?: boolean;
};

export function CustomerNotesHistorySection({
  customerOrgId,
  language = "English",
  onCustomerUpdate,
  defaultHistoryOpen = false,
  queriesEnabled = true,
}: CustomerNotesHistorySectionProps) {
  const [historyOpen, setHistoryOpen] = useState(defaultHistoryOpen);
  const [noteValue, setNoteValue] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [languageValue, setLanguageValue] = useState(language);
  const [isEditingLanguage, setIsEditingLanguage] = useState(false);
  const [searchParams] = useSearchParams();
  const highlightNoteId = Number(searchParams.get("noteId")) || null;

  const { uid, canMutateOrg } = useAuth();
  const currentUserId = uid ? Number(uid) : null;

  const { data: notes = [], isLoading: isHistoryLoading } =
    useCustomerNotesHistoryQuery(customerOrgId, { enabled: queriesEnabled });
  const addNoteMutation = useAddCustomerNoteMutation();
  const updateSettingsMutation = useUpdateCustomerSettingsMutation();

  useEffect(() => {
    setLanguageValue(language || "English");
  }, [language]);

  useEffect(() => {
    if (highlightNoteId) {
      setHistoryOpen(true);
    }
  }, [highlightNoteId]);

  const handleAddNote = async () => {
    const trimmed = noteValue.trim();
    if (!trimmed && selectedFiles.length === 0) return;
    try {
      await addNoteMutation.mutateAsync({
        customerRepId: customerOrgId,
        body: trimmed || undefined,
        files: selectedFiles,
      });
      setNoteValue("");
      setSelectedFiles([]);
      onCustomerUpdate?.();
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const handleSaveLanguage = async () => {
    try {
      await updateSettingsMutation.mutateAsync({
        customerRepId: customerOrgId,
        language: languageValue || "English",
      });
      setIsEditingLanguage(false);
      onCustomerUpdate?.();
    } catch (error) {
      console.error("Error updating language:", error);
    }
  };

  const isSaving =
    addNoteMutation.isPending || updateSettingsMutation.isPending;
  const latestNote = notes[0] ?? null;
  const canSubmitNote = Boolean(noteValue.trim()) || selectedFiles.length > 0;

  return (
    <div className="w-full space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Customer notes
      </h2>
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            onClick={() => setHistoryOpen(!historyOpen)}
          >
            <IconHistory size={20} className="shrink-0 text-teal-700" />
            {isHistoryLoading ? (
              <Loader size="sm" />
            ) : (
              <span className="truncate text-base font-semibold text-slate-800">
                Notes history ({notes.length})
              </span>
            )}
            {historyOpen ? (
              <IconChevronUp
                size={18}
                className="ml-auto shrink-0 text-slate-500"
              />
            ) : (
              <IconChevronDown
                size={18}
                className="ml-auto shrink-0 text-slate-500"
              />
            )}
          </button>
        </div>

        {!historyOpen && latestNote?.body?.trim() ? (
          <p className="mt-3 line-clamp-2 text-sm text-slate-600">
            <CustomerNoteBody body={latestNote.body} />
          </p>
        ) : null}
      </div>

      {historyOpen ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          {isHistoryLoading ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : notes.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-500">
              No notes yet.
            </p>
          ) : (
            <ol className="space-y-4 border-l-2 border-slate-200 pl-4">
              {notes.map((entry) => (
                <CustomerNoteHistoryItem
                  key={entry.id}
                  entry={entry}
                  customerOrgId={customerOrgId}
                  highlight={highlightNoteId === entry.id}
                  canModify={
                    canMutateOrg &&
                    currentUserId !== null &&
                    entry.author_id !== null &&
                    entry.author_id === currentUserId
                  }
                  onUpdated={onCustomerUpdate}
                />
              ))}
            </ol>
          )}
        </div>
      ) : null}

      {canMutateOrg ? (
        <div className="rounded-2xl border border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-4">
            <IconFileText size={18} className="text-slate-500" />
            <span className="text-sm font-medium uppercase tracking-[0.08em] text-slate-600">
              Add note
            </span>
          </div>
          <div className="space-y-3 overflow-visible bg-white px-5 py-4">
            <MentionTextarea
              placeholder="Add a note about this customer... Type @ to mention a teammate."
              value={noteValue}
              onChange={setNoteValue}
              rows={3}
              className="w-full resize-y rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />
            <CustomerNoteAttachmentPicker
              files={selectedFiles}
              onChange={setSelectedFiles}
              disabled={isSaving}
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleAddNote}
                disabled={isSaving || !canSubmitNote}
                className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {addNoteMutation.isPending ? "Saving..." : "Add note"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <span className="text-sm font-medium uppercase tracking-[0.08em] text-slate-600">
            Language
          </span>
          {canMutateOrg && !isEditingLanguage ? (
            <button
              type="button"
              onClick={() => {
                setLanguageValue(language || "English");
                setIsEditingLanguage(true);
              }}
              className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Edit
            </button>
          ) : null}
        </div>
        <div className="bg-white px-5 py-4">
          {isEditingLanguage ? (
            <div className="space-y-3">
              <select
                value={languageValue}
                onChange={(event) =>
                  setLanguageValue(event.target.value || "English")
                }
                className="w-full cursor-pointer rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              >
                {languageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setLanguageValue(language || "English");
                    setIsEditingLanguage(false);
                  }}
                  disabled={isSaving}
                  className="cursor-pointer rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-100 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveLanguage}
                  disabled={isSaving}
                  className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-60"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="w-fit rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              {language || "English"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
